import { clamp } from './utils.js';

/**
 * @typedef {object} PresetState
 * @property {number} beatsPerBar
 * @property {number} subdivision
 * @property {number} secondaryPulses
 * @property {number} secondaryMode
 * @property {number} primaryPulses
 * @property {number} primaryMode
 */

/**
 * @typedef {object} PresetDeps
 * @property {any} el
 * @property {() => PresetState} getState
 * @property {(next: Partial<PresetState>) => void} setState
 * @property {(pct: number) => void} setSwing
 * @property {(beatsPerBar: number) => void} buildBeatLights
 * @property {() => void} updateRhythm
 * @property {() => void} reset
 * @property {(msg: string) => void} logLine
 * @property {(value: boolean) => void} setApplyingPreset
 */

/** @param {PresetDeps} deps */
export function initPresets({
    el,
    getState,
    setState,
    setSwing,
    buildBeatLights,
    updateRhythm,
    reset,
    logLine,
    setApplyingPreset,
}) {
    /** @type {{ id: string, name?: string, time?: { beatsPerBar?: number, subdivision?: number, swing?: number }, accent?: { secondary?: { pulses?: number, mode?: number }, primary?: { pulses?: number, mode?: number } } }[]} */
    let presetList = [];
    /** @type {Map<string, (typeof presetList)[number]>} */
    const presetById = new Map();

    /** @param {string} id */
    function applyPreset(id) {
        const preset = presetById.get(id);
        if (!preset) return;

        setApplyingPreset(true);
        const current = getState();
        const time = preset.time || {};
        const beatsPerBar = clamp(
            Math.trunc(Number(time.beatsPerBar)) || current.beatsPerBar,
            1,
            32,
        );
        const subdivision = clamp(
            Math.trunc(Number(time.subdivision)) || current.subdivision,
            1,
            4,
        );
        const swing = Number(time.swing);
        setSwing(Number.isFinite(swing) ? swing : 0);

        const acc = preset.accent || {};
        const sec = acc.secondary || {};
        const pri = acc.primary || {};
        const secondaryPulses = Math.trunc(Number(sec.pulses)) || 0;
        const secondaryMode = Math.trunc(Number(sec.mode)) || 0;
        const primaryPulses = Math.trunc(Number(pri.pulses)) || 0;
        const primaryMode = Math.trunc(Number(pri.mode)) || 0;

        setState({
            beatsPerBar,
            subdivision,
            secondaryPulses,
            secondaryMode,
            primaryPulses,
            primaryMode,
        });

        el.beatsPerBar.value = String(beatsPerBar);
        el.subdivision.value = String(subdivision);
        el.secondaryPulsesNumber.value = String(secondaryPulses);
        el.secondaryModeNumber.value = String(secondaryMode + 1);
        el.primaryPulsesNumber.value = String(primaryPulses);
        el.primaryModeNumber.value = String(primaryMode + 1);

        buildBeatLights(beatsPerBar);
        updateRhythm();
        reset();
        setApplyingPreset(false);
        logLine(`Preset → ${preset.name || preset.id}`);
    }

    async function loadPresets() {
        if (!el.presetSelect) return;
        try {
            const res = await fetch('./presets.json', { cache: 'no-store' });
            if (!res.ok) throw new Error(`Preset load failed: ${res.status}`);
            const data = await res.json();
            if (!data || !Array.isArray(data.patterns)) throw new Error('Invalid presets format');

            presetList = data.patterns.slice();
            presetById.clear();
            presetList.forEach((p) => {
                if (p && p.id) presetById.set(p.id, p);
            });

            const firstOption = el.presetSelect.querySelector('option[value=""]');
            el.presetSelect.innerHTML = '';
            if (firstOption) el.presetSelect.appendChild(firstOption);
            else {
                const opt = document.createElement('option');
                opt.value = '';
                opt.textContent = 'Presets';
                el.presetSelect.appendChild(opt);
            }
            presetList.forEach((p) => {
                const opt = document.createElement('option');
                opt.value = p.id;
                opt.textContent = p.name || p.id;
                el.presetSelect.appendChild(opt);
            });

            el.presetSelect.value = '';
        } catch (err) {
            logLine('Preset load failed.');
        }
    }

    if (el.presetSelect) {
        el.presetSelect.addEventListener('change', () => {
            const id = el.presetSelect.value;
            if (!id) return;
            applyPreset(id);
        });
    }

    loadPresets();
}
